import { useState, useEffect, useRef } from 'react'
import { HomeworkService, type Homework } from '../services/homework'
import { OpenAIService } from '../services/openai'
import chatIconMark from '../assets/icon/chat-icon-mark.svg'
import ChatPanel from './ChatPanel'

interface ChatMessage {
  id: string
  sender: 'user' | 'ai'
  message: string
  timestamp: string
}

const now = () =>
  new Date().toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })

interface HomeworkChatProps {
  homework: Homework
  studentId: string
  // true when the chat sits inside a white panel instead of on the gradient
  onWhite?: boolean
}

// The homework AI chat: loads the student's persisted conversation for this homework and
// lets them keep chatting. Shared by the answer form (HomeworkDetailPage) and the feedback
// view (HomeworkFeedbackView) so both show the exact same conversation. Renders the section
// header and the white card; the parent owns the surrounding layout/sizing.
const HomeworkChat = ({ homework, studentId, onWhite = false }: HomeworkChatProps) => {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [isAiTyping, setIsAiTyping] = useState(false)
  const conversationHistoryRef = useRef<Array<{ sender: 'user' | 'ai'; message: string }>>([])
  // True until the welcome message has been persisted; lets us defer creating any
  // DB rows until the student actually sends a message (empty visits create nothing).
  const firstSendRef = useRef(false)
  const welcomeTextRef = useRef('')

  useEffect(() => {
    const load = async () => {
      try {
        const initialText = homework.initial_message ||
          `こんにちは！「${homework.title}」について一緒に考えましょう。\n\nまず、このテーマについてあなたの考えや疑問を教えてください！`
        welcomeTextRef.current = initialText

        // Load any previously persisted chat for this student + homework
        const rows = await HomeworkService.getChatMessages(studentId, homework.id)
        if (rows.length > 0) {
          const loaded: ChatMessage[] = rows.map(r => ({
            id: r.id,
            sender: r.role === 'assistant' ? 'ai' : 'user',
            message: r.content,
            timestamp: new Date(r.created_at).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })
          }))
          setMessages(loaded)
          conversationHistoryRef.current = loaded.map(m => ({ sender: m.sender, message: m.message }))
          firstSendRef.current = false
        } else {
          // No history yet: show the welcome in-memory only; persist it on first send
          const welcome: ChatMessage = { id: 'init', sender: 'ai', message: initialText, timestamp: now() }
          setMessages([welcome])
          conversationHistoryRef.current = [{ sender: 'ai', message: initialText }]
          firstSendRef.current = true
        }
      } catch (err) {
        console.error('Could not load homework chat:', err)
      }
    }
    load()
  }, [homework.id, homework.initial_message, homework.title, studentId])

  const sendMessage = async () => {
    if (!input.trim() || isSending) return
    const userMsg: ChatMessage = { id: Date.now().toString(), sender: 'user', message: input.trim(), timestamp: now() }
    const history = [...conversationHistoryRef.current]
    setMessages(prev => [...prev, userMsg])
    conversationHistoryRef.current = [...history, { sender: 'user', message: input.trim() }]
    setInput('')
    setIsSending(true)
    setIsAiTyping(true)

    // Persist the welcome message lazily on the first send, then the user's message.
    // DB failures are logged but never block the chat.
    try {
      if (firstSendRef.current) {
        await HomeworkService.addChatMessage(studentId, homework.id, 'assistant', welcomeTextRef.current)
        firstSendRef.current = false
      }
      await HomeworkService.addChatMessage(studentId, homework.id, 'user', userMsg.message)
    } catch (persistErr) {
      console.warn('Could not persist homework chat message:', persistErr)
    }

    try {
      // Only the id goes over the wire. openai-chat resolves the prompt itself
      // (per-homework override → homework_chat_prompt_base → its built-in default) and
      // appends the title/description, so the text cannot be tampered with in transit.
      const response = await OpenAIService.generateResponse(userMsg.message, {
        homeworkId: homework.id,
        conversationHistory: history
      })
      const aiMsg: ChatMessage = { id: (Date.now() + 1).toString(), sender: 'ai', message: response, timestamp: now() }
      setMessages(prev => [...prev, aiMsg])
      conversationHistoryRef.current = [...conversationHistoryRef.current, { sender: 'ai', message: response }]
      try {
        await HomeworkService.addChatMessage(studentId, homework.id, 'assistant', response)
      } catch (persistErr) {
        console.warn('Could not persist homework chat response:', persistErr)
      }
    } catch (err) {
      console.error(err)
      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(), sender: 'ai',
        message: '申し訳ありません、エラーが発生しました。もう一度お試しください。', timestamp: now()
      }])
    } finally {
      setIsSending(false)
      setIsAiTyping(false)
    }
  }

  // Reset the chat: delete the persisted conversation and return to the welcome message,
  // deferring re-persistence until the next send (same lazy behavior as a first visit).
  const handleReset = async () => {
    if (isSending) return
    const confirmed = window.confirm('チャットの履歴をすべて削除してもよろしいですか？\nこの操作は取り消せません。')
    if (!confirmed) return

    try {
      await HomeworkService.deleteChatMessages(studentId, homework.id)
    } catch (err) {
      console.warn('Could not delete homework chat messages:', err)
    }

    const welcome: ChatMessage = { id: 'init', sender: 'ai', message: welcomeTextRef.current, timestamp: now() }
    setMessages([welcome])
    conversationHistoryRef.current = [{ sender: 'ai', message: welcomeTextRef.current }]
    firstSendRef.current = true
  }

  return (
    <>
      {/* Section header — transparent so gradient shows through */}
      <div className="bg-transparent px-1 py-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <img src={chatIconMark} alt="" style={{ width: 45, height: 40 }} className="flex-shrink-0" />
          <span className={`font-bold text-base ${onWhite ? 'text-gray-900' : 'text-white'}`}>対話しながら考えよう</span>
        </div>
        <button
          onClick={handleReset}
          className={`p-2 rounded-lg transition-colors ${onWhite ? 'hover:bg-gray-100' : 'hover:bg-white/20'}`}
          title="チャットをリセット"
        >
          <svg className={`w-5 h-5 ${onWhite ? 'text-gray-400' : 'text-white'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            <line x1="10" y1="11" x2="10" y2="17" />
            <line x1="14" y1="11" x2="14" y2="17" />
          </svg>
        </button>
      </div>

      {/* White card containing chat */}
      <div className={`flex-1 flex flex-col bg-white rounded-xl overflow-hidden min-h-0 ${onWhite ? 'border border-gray-200' : 'shadow'}`}>
        <ChatPanel
          messages={messages}
          isAiTyping={isAiTyping}
          inputValue={input}
          onInputChange={setInput}
          onSend={sendMessage}
          isSending={isSending}
        />
      </div>
    </>
  )
}

export default HomeworkChat
